import React, { useState, useEffect, useCallback, useRef } from "react";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import Utils from "../../../Common/Utils";
import AppSettings from "../../../Settings/AppSettings";
import Paginator from "../../Common/Paginator";
import DatePicker from "react-datepicker";
import CustomDatePicker from "../../Common/CustomDatePicker";
import "react-datepicker/dist/react-datepicker.css";
import AuthManager from "../../../Common/AuthManager";
import "./UserLogs.css";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
// Paging and search are both server-side, so every keystroke would otherwise be a request.
const SEARCH_DEBOUNCE_MS = 400;

function getDate_as_string(dt) {
  return (
    dt.getFullYear() +
    "-" +
    Utils.appendLeadingZeroes(dt.getMonth() + 1) +
    "-" +
    Utils.appendLeadingZeroes(dt.getDate())
  );
}

// createdAt arrives as an epoch timestamp in SECONDS, so scale to the milliseconds the Date
// constructor expects. Returns null for anything unparseable so callers render a blank cell
// instead of crashing the table.
function created_at_to_date(createdAt) {
  if (createdAt == null || createdAt === "") return null;
  var seconds = Number(createdAt);
  if (!Number.isFinite(seconds)) return null;
  var dt = new Date(seconds * 1000);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

// Rendered in UTC, matching how these timestamps have always been displayed. Swap the getUTC*
// calls for their local equivalents to show the viewer's own timezone instead.
function format_created_at_date(createdAt) {
  var dt = created_at_to_date(createdAt);
  if (dt === null) return "";
  return (
    dt.getUTCFullYear() +
    "-" +
    Utils.appendLeadingZeroes(dt.getUTCMonth() + 1) +
    "-" +
    Utils.appendLeadingZeroes(dt.getUTCDate())
  );
}

function format_created_at_time(createdAt) {
  var dt = created_at_to_date(createdAt);
  if (dt === null) return "";
  return (
    Utils.appendLeadingZeroes(dt.getUTCHours()) +
    ":" +
    Utils.appendLeadingZeroes(dt.getUTCMinutes()) +
    ":" +
    Utils.appendLeadingZeroes(dt.getUTCSeconds())
  );
}

// The backend writes exactly FOUR status literals: Failure 669, Success 479, Info 97,
// Warning 87. Counted by walking the AST of every non-test .py file for the first argument
// of user_activity / logger.create / create_new / log_user_activity, resolving inline
// conditionals and variables assigned one — a grep cannot see
// `status = "Success" if ok else "Failure"` and undercounts badly.
//
// "Info" is not rare — ~29% of user_logs_prod ROWS (a sample of the live collection, which
// is a different measurement from the call-site counts above). Before this mapping existed
// it fell through to the failure branch, so roughly a third of this table rendered routine
// informational rows as red errors with a ✗ icon, which made the log hard to scan for the
// real problems it exists to surface.
//
// Three one-off spellings were collapsed into these four on the backend (2026-10-01):
// "Error" (5 sites, all alongside a failed job) and "Partial Failure" / "Partial Success"
// (5 sites) are now written as Failure. Historical "Error" rows were deliberately NOT
// rewritten — an audit trail should not be edited — so ~10 of them still exist on prod and
// land in the fallback below, which is the right place for them. "Partial *" never had a
// single stored row in any environment, so nothing renders them.
//
// Anything unrecognised keeps falling through to failure ON PURPOSE. A neutral default
// would hide a real error behind a grey pill; red is the safe way to be wrong. Do not
// "tidy" the fallback.
export function status_class(status) {
  var s = (status || "").toString().toLowerCase();
  if (s === "success") return "user-logs__status--success";
  if (s === "info") return "user-logs__status--info";
  if (s === "warning") return "user-logs__status--warning";
  return "user-logs__status--failure";
}

export function status_icon(status) {
  var s = (status || "").toString().toLowerCase();
  if (s === "success") return "fa fa-check-circle";
  if (s === "info") return "fa fa-info-circle";
  if (s === "warning") return "fa fa-exclamation-triangle";
  return "fa fa-times-circle";
}

function Logs() {
  var initialStart = new Date();
  initialStart.setDate(initialStart.getDate() - 6);

  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(new Date());
  // The committed range — only a Search click copies the pickers into this, so changing a picker
  // doesn't fire a request until the user asks for it.
  const [range, setRange] = useState({
    startdate: getDate_as_string(initialStart),
    enddate: getDate_as_string(new Date()),
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState("");

  const startDateref = React.createRef();
  const endDateref = React.createRef();
  const pageRange = 10;

  // Debounce the search box into the term the request actually uses, and send the user back to
  // page 1 — page 7 of the old result set is meaningless against a new filter.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Guards against the stale response of a superseded request overwriting a newer one.
  const requestSeq = useRef(0);

  const loadLogList = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    let api_base_url = AppSettings.GMS_BACKEND_API_URL;
    let ret = await ServiceWrapper.doPost(
      api_base_url + "user/logs/list",
      {
        startdate: range.startdate,
        enddate: range.enddate,
        page: page,
        limit: recordPerPage,
        search: searchTerm,
      },
      null,
      AuthManager.getGmsToken()
    );
    if (seq !== requestSeq.current) return;
    const payload = ret.data || {};
    // check_rbac denies with HTTP 200 + {done:false, message:"Permission denied!!"}, so a refusal
    // is NOT an HTTP error. Without this branch it read as zero rows and rendered "No logs found",
    // which is misleading. The page guard in Pages/Admin/Logs/Index.jsx normally stops us getting
    // here, but a stale JWT can still disagree with the backend.
    const refused = !ret.errorfound && payload.done === false;
    setLoadError(ret.errorfound || refused);
    setLoadErrorMessage(refused ? payload.message || "" : "");
    if (!ret.errorfound && !refused) {
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(Number(payload.total) || 0);
      // The backend clamps an out-of-range page into the result set; mirror that back so the
      // paginator highlights the page actually being shown.
      if (payload.page && payload.page !== page) setPage(payload.page);
    }
    setLoading(false);
  }, [range, page, recordPerPage, searchTerm]);

  useEffect(() => {
    loadLogList();
  }, [loadLogList]);

  function apply_date_range() {
    setRange({
      startdate: getDate_as_string(startDate),
      enddate: getDate_as_string(endDate),
    });
    setPage(1);
  }

  // Paginator slices client-side and hands back the page's rows; here the server already did the
  // slicing, so only the page number matters. Ignoring a same-page callback avoids a duplicate
  // request when the paginator re-syncs after `total` changes.
  function handle_page_change(pagenumber) {
    if (pagenumber === page) return;
    setPage(pagenumber);
  }

  function handle_recordsperpage_change(newval) {
    setRecordPerPage(parseInt(newval, 10));
    setPage(1);
  }

  const firstRow = total === 0 ? 0 : (page - 1) * recordPerPage + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;

  return (
    <div className="user-logs">
      <div className="user-logs__toolbar">
        <div className="user-logs__field">
          <span className="user-logs__label">Date range</span>
          <div className="user-logs__dates">
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              maxDate={endDate}
              customInput={<CustomDatePicker ref={startDateref} />}
              popperProps={{ strategy: 'fixed' }}
            />
            <span className="user-logs__dates-sep">to</span>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              minDate={startDate}
              customInput={<CustomDatePicker ref={endDateref} />}
              popperProps={{ strategy: 'fixed' }}
            />
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={apply_date_range}
            >
              <span className="fa fa-search" />
              &nbsp;Search
            </button>
          </div>
        </div>

        <div className="user-logs__toolbar-end">
          <div className="user-logs__field">
            <span className="user-logs__label">Filter</span>
            {/* The icon is positioned against this wrapper — not the whole field — so it centres
                on the input rather than on the label row above it. */}
            <div className="user-logs__search">
              <span className="fa fa-search" />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="User, module, description…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>

          <div className="user-logs__field">
            <span className="user-logs__label">Rows</span>
            <select
              className="form-select form-select-sm"
              value={recordPerPage}
              onChange={(e) => handle_recordsperpage_change(e.target.value)}
            >
              {PAGE_SIZE_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Only shown before the first result set exists. Once rows are on screen the table stays
          mounted and dims instead — unmounting it would remount <Paginator/>, whose mount effect
          fires onPageChange(1) and would silently reset the page on every fetch. */}
      {loading && total === 0 && !loadError && (
        <div className="user-logs__state">
          <span className="fas fa-circle-notch fa-spin" />
          <span className="user-logs__state-title">Loading logs…</span>
        </div>
      )}

      {!loading && loadError && (
        <div className="user-logs__state user-logs__state--error">
          <span className="fa fa-exclamation-triangle" />
          <span className="user-logs__state-title">Couldn’t load logs</span>
          <span>
            {loadErrorMessage ||
              "Something went wrong fetching this date range. Try again."}
          </span>
        </div>
      )}

      {!loading && !loadError && total === 0 && (
        <div className="user-logs__state">
          <span className="fa fa-clipboard" />
          <span className="user-logs__state-title">No logs found</span>
          <span>
            {searchTerm
              ? `Nothing matches “${searchTerm}” between ${range.startdate} and ${range.enddate}.`
              : `No activity was recorded between ${range.startdate} and ${range.enddate}.`}
          </span>
        </div>
      )}

      {!loadError && total > 0 && (
        <div
          className={
            "user-logs__results" + (loading ? " user-logs__results--busy" : "")
          }
          aria-busy={loading}
        >
          <div className="user-logs__resultbar">
            <div className="user-logs__summary">
              <span className="user-logs__count">{total.toLocaleString()}</span>
              <span>
                {total === 1 ? "entry" : "entries"} from {range.startdate} to{" "}
                {range.enddate}
              </span>
              <span>
                · showing {firstRow.toLocaleString()}–{lastRow.toLocaleString()}
              </span>
              {loading && (
                <span className="user-logs__busy">
                  <span className="fas fa-circle-notch fa-spin" />
                  &nbsp;updating…
                </span>
              )}
            </div>

            <Paginator
              recordPerPage={recordPerPage}
              totalRecords={total}
              data={[]}
              pageRange={pageRange}
              presentPage={page}
              onPageChange={handle_page_change}
            />
          </div>

          <div className="user-logs__table-wrap">
            <table className="table user-logs__table">
              <thead>
                <tr>
                  <th style={{ width: "12%" }}>User</th>
                  <th style={{ width: "9%" }}>Status</th>
                  <th>Module / Description</th>
                  <th style={{ width: "9%" }}>From</th>
                  <th style={{ width: "11%" }}>On (UTC)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item, index) => (
                  <tr key={"logs" + index}>
                    <td className="user-logs__user">{item.username || "—"}</td>
                    <td>
                      <span
                        className={
                          "user-logs__status " + status_class(item.status)
                        }
                      >
                        <span className={status_icon(item.status)} />
                        {item.status || "Unknown"}
                      </span>
                    </td>
                    <td>
                      <div>
                        <span className="user-logs__module">
                          {item.module || "—"}
                        </span>
                        {item.nodeId ? (
                          <span className="user-logs__node">{item.nodeId}</span>
                        ) : null}
                      </div>
                      <div className="user-logs__desc">{item.description}</div>
                    </td>
                    <td>
                      <span className="user-logs__source">
                        {item.fromSource || "—"}
                      </span>
                    </td>
                    <td className="user-logs__date">
                      {format_created_at_date(item.createdAt)}
                      <span className="user-logs__time">
                        {format_created_at_time(item.createdAt)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Logs;
